import React, { createContext, useContext, useState, ReactNode } from 'react'
import { useGestureWithDragDetection } from '../hooks/useGestureWithDragDetection'
import { GestureHandlers, GestureOptions, DragEventData } from '../types/gesture-types'

// Define the context type
interface GestureContextType {
  isDragging: boolean
  bindGesture: (handlers: GestureHandlers, options?: GestureOptions) => any
}

// Create the context with a default value
const GestureContext = createContext<GestureContextType | undefined>(undefined)

interface GestureProviderProps {
  children: ReactNode
}

/**
 * Provider component that manages the gesture state and provides
 * the gesture bindings to any child component that needs them
 */
export const GestureProvider: React.FC<GestureProviderProps> = ({ children }) => {
  const [isDragging, setIsDragging] = useState(false)

  // Provide a function that returns a gesture binding with dragging state management
  const bindGesture = (handlers: GestureHandlers, options: GestureOptions = {}) => {
    const wrappedHandlers: GestureHandlers = {
      onDragStart: (args: DragEventData) => {
        setIsDragging(true)
        handlers.onDragStart?.(args)
      },
      onDragMove: handlers.onDragMove,
      onDragEnd: (args: DragEventData) => {
        setIsDragging(false)
        handlers.onDragEnd?.(args)
      },
      onTap: handlers.onTap,
    }

    return useGestureWithDragDetection(wrappedHandlers, options)
  }

  const contextValue: GestureContextType = {
    isDragging,
    bindGesture,
  }

  return <GestureContext.Provider value={contextValue}>{children}</GestureContext.Provider>
}

/**
 * Custom hook to use the gesture context
 */
export const useGesture = () => {
  const context = useContext(GestureContext)
  if (context === undefined) {
    throw new Error('useGesture must be used within a GestureProvider')
  }
  return context
}
